import split from 'lodash/split';
import axios from 'axios';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import * as types from '../constants/ActionTypes';
import Page from '../../lib/page';
import { getContextId, isSetContextEnabled } from '../../lib/omnichannelHelper';
import { getIcfMappings } from '../../home/actions/action-icf-guide';
import { trackLink, trackPage } from '../../lib/analytics-helper';
import { getNNAIcfId } from '../../lib/nnaHelper';
import getHistory from '../../lib/HistoryHelper';
import {
  isOmniChannelAPITriggeredInSession,
  removeOmniChannelAPITriggeredInSession,
  omniChannelAPITriggeredInSession,
  parseOmniChannelIntents,
} from '../../lib/globalHelper';
import UrlResources from '../../home/constants/UrlResources';
import updateBnaData from '../../home/actions/action-update-bna-data';
import { PageMapping } from '../../lib/page-name-identifiers';
import updateUserSelection from '../../../experimentations/updateUserSelection';
import { isBotExperienceEnabled, isShowOnlySearchLayoutEnabled } from '../../../experimentations/helper';
import {
  ECM_DOC_ID_KEY,
  ECM_COMPONENT_IDS_KEY,
  ECM_DOC_ID_DEFAULT_VALUE,
  ECM_COMPONENT_IDS_DEFAULT_VALUE,
} from '../constants/ecmFPTITagsKeys';

const CancelToken = axios.CancelToken;
const source = CancelToken.source();

const ARTICLE_URL = '/smarthelp/article-content';

export function requestArticle() {
  return {
    type: types.REQUEST_ARTICLE,
    payload: undefined,
  };
}

export function receiveArticle(responseData) {
  return {
    type: types.RECEIVE_ARTICLE_RESPONSE,
    payload: responseData,
  };
}

export function receiveArticleError() {
  return {
    type: types.RECEIVE_ARTICLE_ERROR,
    payload: undefined,
  };
}

export function resetOmniChannelPromptInfo() {
  return {
    type: types.RESET_OMNI_CHANNEL_PROMPT_INFO,
  };
}

const setContextIntents = (params) => ({
  type: types.SET_CONTEXT_INTENTS,
  payload: params,
});

const setContextResponse = (response) => ({
  type: types.SET_CONTEXT_RESPONSE,
  payload: response,
});

export const resetOcpContextResponse = () => ({
  type: types.RESET_OCP_CONTACT_RESPONSE,
});

/**
 * Set context POST call with available tags information
 */
export function setContextApiCall(
  intents,
  faq,
  customerConfirmation = false,
  isTrackersData = false,
  trackersData = {},
  fptiDocId = '',
  fptiComponentIds = '',
) {
  return (dispatch) => {
    dispatch(setContextIntents({ intents, faq }));
    const deviceType = Page.getDeviceType();
    // Returning customer conformed true only in contact us page or
    // if the article is clicked which has intents in search page
    const customerConfirmed = customerConfirmation || Page.isContactUsPage();
    const fptiPageId = Page.getFPTIPageId();

    //  call set context service only when - we have intents AND faq OR trackers data
    if (Array.isArray(intents) && intents.length && (faq || (isTrackersData && trackersData))) {
      const setContextOptions = {
        _csrf: document.body.getAttribute('data-token'),
        intents,
        deviceType,
        faq,
        isTrackersData,
        trackersData,
      };

      // only if customer is confirmed we will be sending the customer confirmed key
      if (customerConfirmed) {
        setContextOptions.customerConfirmed = customerConfirmed;
      }

      // FPTI For Set context
      // For Contact-us final intent, changing set context as activity event
      if (Page.isContactUsPage()) {
        trackPage({
          id: 'setContextcontactUsPage',
          link: 'smarthelp-set-context',
          ocp_context_metadata: JSON.stringify(intents),
          context_status: 'ACTIVE',
          link_name: customerConfirmed,
          channel_name: 'HELP_CENTER',
          hpid: faq,
          help_doc_id: fptiDocId,
          help_component_ids: fptiComponentIds,
        });
      } else {
        trackPage({
          id: `setContext${fptiPageId}`,
          link: 'smarthelp-set-context',
          ocp_context_metadata: JSON.stringify(intents),
          context_status: 'ACTIVE',
          link_name: customerConfirmed,
          channel_name: 'HELP_CENTER',
          hpid: faq,
          help_doc_id: fptiDocId,
          help_component_ids: fptiComponentIds,
        });
      }
      return axios
        .post('/smarthelp/set-context', setContextOptions)
        .then((response) => {
          // Fpti when set context is success
          trackPage({
            id: `setContextSuccess${fptiPageId}`,
            link: 'smarthelp-set-context-success',
            hpid: faq,
            help_doc_id: fptiDocId,
            help_component_ids: fptiComponentIds,
          });
          if (Page.isContactUsPage() && response.data && response.data.contexts && response.data.contexts.items) {
            const setContextFilteredResponse = getContextId(intents, response.data.contexts.items);
            return dispatch(setContextResponse(setContextFilteredResponse));
          }
          return null;
        })
        .catch(() => {
          // Fpti when set context is failed
          trackPage({
            id: `setContextFailure${fptiPageId}`,
            link: 'smarthelp-set-context-failure',
            hpid: faq,
            help_doc_id: fptiDocId,
            help_component_ids: fptiComponentIds,
          });
        });
    }
    // if the required information is not available for firing set context call, then firing an FPTI
    return trackPage({
      id: `omniChannelSetContextNotFired${fptiPageId}`,
      link: 'smarthelp-set-context-not-fired',
      hpid: faq,
      help_doc_id: fptiDocId,
      help_component_ids: fptiComponentIds,
    });
  };
}

/**
 * Set context POST call with available tags information FOR EXP
 */
export function setContextApiCallForExp(intents, faq, customerConfirmation = false) {
  return (dispatch) => {
    dispatch(setContextIntents({ intents, faq }));
    const deviceType = Page.getDeviceType();
    // Returning customer conformed true only in contact us page or
    // if the article is clicked which has intents in search page
    const customerConfirmed = customerConfirmation || Page.isContactUsPage();
    const fptiPageId = Page.getFPTIPageId();

    // Only if we have intents and faq call set context service
    if (Page.isLoggedIn() && !_isEmpty(intents)) {
      const setContextOptions = {
        _csrf: document.body.getAttribute('data-token'),
        intents,
        deviceType,
      };

      // only if customer is confirmed wee will be sending the customer confirmed key
      if (customerConfirmed) {
        setContextOptions.customerConfirmed = customerConfirmed;
      }

      // FPTI For Set context
      // For Contact-us final intent, changing set context as activity event
      trackPage({
        id: `setContext${fptiPageId}`,
        link: 'smarthelp-set-context',
        ocp_context_metadata: JSON.stringify(intents),
        context_status: 'ACTIVE',
        link_name: customerConfirmed,
        channel_name: 'HELP_CENTER',
      });
      return axios
        .post('/smarthelp/set-context', setContextOptions)
        .then((response) => {
          // Fpti when set context is success
          trackPage({ id: `setContextSuccess${fptiPageId}`, link: 'smarthelp-set-context-success' });
          if (Page.isContactUsPage() && response.data && response.data.contexts && response.data.contexts.items) {
            const setContextFilteredResponse = getContextId(intents, response.data.contexts.items);
            return dispatch(setContextResponse(setContextFilteredResponse));
          }
          return null;
        })
        .catch(() => {
          // Fpti when set context is failed
          trackPage({ id: `setContextFailure${fptiPageId}`, link: 'smarthelp-set-context-failure' });
        });
    }
    // if the required information is not available for firing set context call, then firing an FPTI
    return trackPage({ id: `omniChannelSetContextNotFired${fptiPageId}`, link: 'smarthelp-set-context-not-fired' });
  };
}

export function fetchArticle(faqId, checkTechnical = false) {
  return (dispatch, getState) => {
    dispatch(requestArticle());
    const faq = split(faqId, ',')[0];
    const isHomePage = !!window.localStorage.getItem('isHome');

    // taking the flag set in updatebna and passing it as a param for the article-content
    const isPCC = !!window.localStorage.getItem('isBNA') || faq.toUpperCase().indexOf('PCC') === 0;
    let articleURL = `${ARTICLE_URL}?article_id=${faq}&isPCC=${isPCC}&isHomePage=${isHomePage}`;

    const topicDetails = _get(getState(), 'selectResult.topicTreeSelectResult', {});

    // Checking the session storage for omni channel API triggered value from session
    const setContextTriggered = isOmniChannelAPITriggeredInSession();

    if (checkTechnical) {
      articleURL = Page.setChannelForTechnicalExp(articleURL);
    }

    return axios
      .get(articleURL, {
        cancelToken: source.token,
      })
      .then((response) => {
        // Removing the isBna flag since we have already passed it as a param to article-content
        window.localStorage.removeItem('isBNA');
        if (response.data && response.data.articleContent) {
          window.localStorage.removeItem('isHome');
          dispatch(receiveArticle(response.data.articleContent));
          // Innovation Guild: search only experiments enabled for show FAQ result or launching bot.
          if (isShowOnlySearchLayoutEnabled() || isBotExperienceEnabled()) {
            const topicId = response.data.articleContent.topicId;
            const topicName = response.data.articleContent.topicName;
            dispatch(updateUserSelection(topicId, topicName));
          }

          const articleIntents = isSetContextEnabled(topicDetails, response.data);

          let isCustomerConfirmed = false;
          const customerSaysIntent = _get(getState(), 'searchResult.items[0].customerSaysIntent', '');
          let intentPredictions = _get(response, 'data.articleContent.tags[0].predictions', []);
          // verify if the customerSaysIntent matches with article intent then set the CustomerConfirmed true for setContext API
          if (Array.isArray(intentPredictions) && intentPredictions.length > 0) {
            intentPredictions = parseOmniChannelIntents(intentPredictions);

            // Checking the customersays intent and top intent from the Search
            isCustomerConfirmed = _isEqual(
              customerSaysIntent?.toLowerCase(),
              intentPredictions[0].customer_intent?.toLowerCase(),
            );
          } else {
            // omni-channel not fired FPTI for search page
            trackPage({ id: 'omniChannelSetContextNotFiredsearchPage', link: 'smarthelp-set-context-not-fired', faq });
          }

          // If set context API already triggered when clicked on search results do not trigger the API again
          if (!setContextTriggered && Page.isLoggedIn()) {
            // get the ECM API tags from article data
            const fptiDocId = response.data.articleContent[ECM_DOC_ID_KEY] || ECM_DOC_ID_DEFAULT_VALUE;
            const fptiComponentIds =
              response.data.articleContent[ECM_COMPONENT_IDS_KEY] || ECM_COMPONENT_IDS_DEFAULT_VALUE;

            dispatch(
              setContextApiCall(articleIntents, faq, isCustomerConfirmed, false, {}, fptiDocId, fptiComponentIds),
            );
          }

          // Removing OmniChannel API triggered value from session
          removeOmniChannelAPITriggeredInSession();
        } else if (isHomePage) {
          window.localStorage.removeItem('isHome');
          trackPage({ id: 'articleRedirect', hpid: faqId, link: 'smarthelp-article-redirect-to-home' });
          window.location.href = '/smarthelp/home';
        }
      })
      .catch(() => {
        window.localStorage.removeItem('isHome');
        window.localStorage.removeItem('isBNA');
        dispatch(receiveArticleError());
        const articleIntents = isSetContextEnabled(topicDetails, {});
        // Innovation Guild: search only experiments enabled for show FAQ result or launching bot.
        if (isShowOnlySearchLayoutEnabled() || isBotExperienceEnabled()) {
          dispatch(updateUserSelection('GENPOOL', 'HELP_CENTER'));
        }
        // If set context API already triggered when clicked on search results do not trigger the API again
        if (!setContextTriggered && Page.isLoggedIn()) {
          dispatch(setContextApiCall(articleIntents, faq, false));
        }

        // Removing OmniChannel API triggered value from session
        removeOmniChannelAPITriggeredInSession();
      });
  };
}

export function cancelArtilceRequest() {
  source.cancel('article request canceled.');
}

/**
 * Update context POST call with available intents information
 */
export function updateContext(
  articleContent,
  icfId,
  customerConfirmed,
  authFlowContextId = '',
  stepUpRequestedTime = '',
) {
  const intents = (articleContent && articleContent.channelDetails) || '';
  const deviceType = Page.getDeviceType();
  let pathName = PageMapping.home;
  if (Page.isContactUsPage()) {
    pathName = PageMapping['contact-us'];
  }
  // if icf id exists pass icf id else pass the article id
  const faq = icfId || articleContent.articleId;

  // get the ECM API tags from article data
  const fptiDocId = (articleContent && articleContent[ECM_DOC_ID_KEY]) || ECM_DOC_ID_DEFAULT_VALUE;
  const fptiComponentIds = (articleContent && articleContent[ECM_COMPONENT_IDS_KEY]) || ECM_COMPONENT_IDS_DEFAULT_VALUE;

  // Only if we have intents and faq call update context service
  if (Page.isLoggedIn() && !_isEmpty(intents) && faq) {
    // setting the session variable when omni channel API is triggered, only when customerConfirmed is true
    // customerConfirmed will be false only when NO is clicked on the omni channel prompt
    if (customerConfirmed) {
      omniChannelAPITriggeredInSession(true);
    }

    const timeStamp = Math.round(+new Date() / 1000);

    const updateContextReqParams = {
      _csrf: document.body.getAttribute('data-token'),
      intents,
      deviceType,
      faq,
      customerConfirmed,
    };

    // If authFlowContextId and stepUpRequestedTime exists adding them to update context request params
    // these values are available once step up up is initiated
    // and after step up is done and while redirecting to help center page we will be making update context call by adding these params
    if (authFlowContextId && stepUpRequestedTime) {
      updateContextReqParams.authFlowContextId = authFlowContextId;
      updateContextReqParams.stepUpRequestedTime = stepUpRequestedTime;
      // changing the pathname to fire step up update context API.
      pathName = 'StepUp';
    }

    // FPTI For update context
    trackLink({
      id: `updateContext${pathName}`,
      link: 'smarthelp-update-context',
      ocp_context_metadata: JSON.stringify(intents),
      timestamp: timeStamp,
      context_status: 'ACTIVE',
      link_name: customerConfirmed,
      channel_name: 'HELP_CENTER',
      help_doc_id: fptiDocId,
      help_component_ids: fptiComponentIds,
    });

    return axios
      .post('/smarthelp/update-context', updateContextReqParams)
      .then(() => {
        // Fpti when update context is success
        trackPage({
          id: `updateContextSuccess${pathName}`,
          link: 'smarthelp-update-context-success',
          hpid: faq,
          help_doc_id: fptiDocId,
          help_component_ids: fptiComponentIds,
        });
      })
      .catch(() => {
        // Fpti when update context is failed
        trackPage({
          id: `updateContextFailure${pathName}`,
          link: 'smarthelp-update-context-failure',
          hpid: faq,
          help_doc_id: fptiDocId,
          help_component_ids: fptiComponentIds,
        });
      });
  }

  // if the required information is not  available for firing update context call, thne firing an FPTI call
  return trackPage({
    id: `omniChannelUpdateContextNotFired${pathName}`,
    link: 'smarthelp-update-context-not-fired',
    hpid: faq,
    help_doc_id: fptiDocId,
    help_component_ids: fptiComponentIds,
  });
}

/**
 * Function to return BNA-ID if ICF-ID is passed
 * @param icfId
 * @returns {Array}
 */
function returnBnaId(icfId) {
  const bnaMapping = getIcfMappings();
  const bnaId = [];
  Object.keys(bnaMapping).forEach((key) => {
    if (bnaMapping[key] === icfId) {
      // Pushing all the BNA's which match ICF articles
      bnaId.push(key);
    }
  });
  return bnaId;
}

/**
 * Function to get BNA article mapped to ICF and call update con text API
 */
export function icfArticleUpdateContext(icfArticle, articles, isNNAIcf = false) {
  // This is the common update context for regular ICF's and NNA ICF. If update context is initiated from NNA article
  // get icf id from remote config. if not get the icfId from icf article.
  // By default isNNAIcf will false
  let icfId = null;
  if (isNNAIcf) {
    icfId = getNNAIcfId();
  } else {
    icfId = icfArticle && icfArticle.icf_id;
  }

  if (icfId) {
    // get bnaid with icfid
    const BnaId = returnBnaId(icfId);
    /* eslint-disable array-callback-return */
    // eslint-disable-next-line consistent-return
    return articles.filter((article) => {
      // checking for BNA ID in the list of BNA id returned from icf remote config mapping
      if (article.channel === 'BNA' && BnaId.indexOf(article.channelDetails.bnaId) >= 0) {
        return updateBnaData(article);
      }
    });
  }
  return trackPage({ id: 'updateContextFailure', link: 'smarthelp-update-context-failure-null-icf-id' });
}

/**
 * Function triggered when clicked on yes on omnichannel prompt in home page
 */
export function updateContextPromptConfirmation(articleInfo, intentCode) {
  return (dispatch) => {
    const faqId = articleInfo.articleId || '';
    // FPTI
    trackLink({
      id: 'omniChannelPromptDisplayed',
      link: `smarthelp-omnichannel-prompt-yes|suggested-solution-${faqId}`,
      ocp_context_metadata: intentCode,
    });

    // Set/update context
    updateContext(articleInfo, '', true);
    dispatch(resetOmniChannelPromptInfo());

    // If FaqId exists redirect to article page
    if (faqId) {
      return getHistory().push(`/${UrlResources.ARTICLE}/${faqId}`);
    }
    // if faq doesnt exists closing the omnichannel prompt and fpti is triggered
    return trackPage({
      id: 'omniChannelPromptDisplayed',
      link: 'smarthelp-omnichannel-prompt-yes-faq-not-available',
      ocp_context_metadata: intentCode,
    });
  };
}
/**
 * Function triggered when clicked on yes on omnichannel prompt in home page
 */
export function updateContextPromptRejection(articleInfo, intentCode) {
  return (dispatch) => {
    const faqId = articleInfo.articleId || '';
    trackLink({
      id: 'omniChannelPromptDisplayed',
      link: `smarthelp-omnichannel-prompt-clicked-no|suggested-solution-${faqId}`,
      ocp_context_metadata: intentCode,
    });
    dispatch(resetOmniChannelPromptInfo());
    return updateContext(articleInfo, '', false);
  };
}
